import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustodiedKind } from '@hangar/shared';
import { listSigningCredentials, revokeSigningCredential, rotateSigningCredential, uploadSigningCredential, type SigningUpload } from './endpointsSigning';

const signingKey = (projectId: string) => ['signing-credentials', projectId] as const;

/** The project's signing credentials, Apple and Android (metadata only). */
export const useSigningCredentials = (projectId: string | undefined, enabled = true) =>
  useQuery({ queryKey: signingKey(projectId ?? ''), queryFn: () => listSigningCredentials(projectId ?? ''), enabled: Boolean(projectId) && enabled, staleTime: 30_000 });

export type SigningAction =
  | { type: 'upload'; kind: CustodiedKind; appId: string; name: string; upload: SigningUpload; idempotencyKey: string }
  | { type: 'rotate'; credentialId: string; upload: SigningUpload };

/** Upload, rotate and revoke (platform operators only; the API refuses everyone else with 403). Every one refreshes the list. */
export function useSigningMutations(projectId: string) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: signingKey(projectId) });
  const save = useMutation({
    mutationFn: (a: SigningAction) => (a.type === 'upload' ? uploadSigningCredential(a.kind, a.appId, a.name, a.upload, a.idempotencyKey) : rotateSigningCredential(a.credentialId, a.upload)),
    onSettled: refresh,
  });
  const revoke = useMutation({ mutationFn: (credentialId: string) => revokeSigningCredential(credentialId), onSettled: refresh });
  return { save, revoke };
}
